// Powers — what you have, and what is holding it up (PLAN-UI §28.3).
//
// **Not buttons.** A power is not a thing you spend, so it has no place in a
// hotbar; §28.3's ruling is that each one surfaces as the thing it enables (the
// orders flag while a banner stands, Sacrifice in a piece's context popover)
// and this screen is the one place that answers "what do I have" as a list.
//
// It is a *reading of the board*, handed in as rows: `on` asks the standing
// structures and nothing else, which is what makes "capture the structure and
// the power goes with it" true by construction rather than by anybody
// remembering to refresh a list. The rows are built where the board is read
// (`PauseMenuZone`); this only draws them.

import { SettingRow, styled } from "../kit";
import { Copy, ScreenTitle } from "./text";

/** One power as the screen lists it. The shape `SessionView.powers` should take. */
export interface PowerRow {
  id: string;
  glyph: string;
  name: string;
  /** What grants it, by name. */
  structure: string;
  /** Standing under your control right now. */
  on: boolean;
}

/**
 * ON / NOT BUILT, in the display voice.
 *
 * Cream for on rather than a green: the palette has no green, and minting one
 * here would be a colour that means "good" in exactly one place.
 */
const PowerState = styled("span", {
  shouldForwardProp: (prop: string) => prop !== "on"
})<{ on?: boolean }>(({ theme, on }) => ({
  fontFamily: theme.kit.font.display,
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: ".08em",
  flex: "none",
  color: on ? theme.kit.color.cream : theme.kit.color.inkDim
}));

/** `powers` is null outside a run: there is nothing to hold. */
export function PowersScreen({ powers }: { powers: readonly PowerRow[] | null }) {
  return (
    <div data-testid="pause-screen-powers">
      <ScreenTitle>Powers</ScreenTitle>
      {powers ? (
        <>
          <Copy>
            A power is on while the structure that grants it stands under your
            control — capture the structure and the power goes with it.
          </Copy>
          {powers.map((power) => (
            <SettingRow
              key={power.id}
              data-testid={`power-row-${power.id}`}
              data-on={power.on}
            >
              <span>
                {power.glyph} {power.name} — {power.structure}
              </span>
              <PowerState on={power.on}>{power.on ? "ON" : "NOT BUILT"}</PowerState>
            </SettingRow>
          ))}
        </>
      ) : (
        <Copy>
          Powers are part of a run. In a scenario or a versus game there is
          nothing to hold.
        </Copy>
      )}
    </div>
  );
}
